import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";
import { axiosClient } from "../AxiosClient";
import { PRAMI_COLORS } from "../expedicionCertificados/certificadosTheme";
import {
  COMPONENT_LABELS,
  allocateCondonationByPriority,
  findHierarchyViolation,
  formatMoney,
  formatNumberToInput,
  formatNumericInput,
  getHierarchyBlockers,
  getLaterComponents,
  hierarchyBlockersLabel,
  isComponentFullyCondoned,
  parseNumericInput,
  roundMoney,
  squareFieldSx,
} from "./condonationFormat";

const ButtonAdd = styled(Button)({
  borderRadius: "20px !important",
  backgroundColor: "#161e55 !important",
  color: "#ffff !important",
  "&:hover": { backgroundColor: "#A8C8AF !important" },
});

const accordionSx = {
  borderRadius: "0 !important",
  border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
  boxShadow: "none",
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
};

const COMPONENT_FIELDS = [
  {
    component: "GAC",
    percentKey: "gacPercent",
    amountKey: "gacAmount",
    pendingKey: "pendingGac",
    quotaKeys: ["gac", "gacValue"],
  },
  {
    component: "MORA",
    percentKey: "moraPercent",
    amountKey: "moraAmount",
    pendingKey: "pendingMora",
    quotaKeys: ["mora", "moraValue"],
  },
  {
    component: "INTERES_CORRIENTE",
    percentKey: "interestPercent",
    amountKey: "interestAmount",
    pendingKey: "pendingInterest",
    quotaKeys: ["interest", "interesCorriente", "currentInterest"],
  },
  {
    component: "FGA",
    percentKey: "fgaPercent",
    amountKey: "fgaAmount",
    pendingKey: "pendingFga",
    quotaKeys: ["fga", "fgaValue"],
  },
  {
    component: "SEGURO_VIDA",
    percentKey: "lifeInsurancePercent",
    amountKey: "lifeInsuranceAmount",
    pendingKey: "pendingLifeInsurance",
    quotaKeys: ["lifeInsurance", "seguroVida"],
  },
  {
    component: "SEGURO_DESEMPLEO",
    percentKey: "unemploymentInsurancePercent",
    amountKey: "unemploymentInsuranceAmount",
    pendingKey: "pendingUnemploymentInsurance",
    quotaKeys: ["unemploymentInsurance", "seguroDesempleo"],
  },
  {
    component: "CAPITAL",
    percentKey: "capitalPercent",
    amountKey: "capitalAmount",
    pendingKey: "pendingCapital",
    quotaKeys: [],
  },
];

const emptyForm = {
  expirationDate: "",
  freezeGacAndMora: true,
  gacPercent: "",
  gacAmount: "",
  moraPercent: "",
  moraAmount: "",
  interestPercent: "",
  interestAmount: "",
  fgaPercent: "",
  fgaAmount: "",
  lifeInsurancePercent: "",
  lifeInsuranceAmount: "",
  unemploymentInsurancePercent: "",
  unemploymentInsuranceAmount: "",
  capitalPercent: "",
  capitalAmount: "",
  reason: "",
};

const emptyComponentInputModes = COMPONENT_FIELDS.reduce((modes, field) => {
  modes[field.component] = "amount";
  return modes;
}, {});

const emptyCondoneAllByComponent = COMPONENT_FIELDS.reduce((acc, field) => {
  acc[field.component] = false;
  return acc;
}, {});

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const expectedForField = (field, quota, pending) => {
  const pendingValue = toNumber(pending[field.pendingKey]);
  if (pendingValue > 0) {
    return pendingValue;
  }
  for (const key of field.quotaKeys || []) {
    const quotaValue = toNumber(quota[key]);
    if (quotaValue > 0) {
      return quotaValue;
    }
  }
  return pendingValue;
};

export default function OfertaDialog({ open, activeQuota, onClose, onSuccess }) {
  const [componentInputModes, setComponentInputModes] = useState(emptyComponentInputModes);
  const [condoneAllByComponent, setCondoneAllByComponent] = useState(emptyCondoneAllByComponent);
  const [componentConfigs, setComponentConfigs] = useState([]);
  const [condoneAllQuota, setCondoneAllQuota] = useState(false);
  const [quotaDiscountInput, setQuotaDiscountInput] = useState("");
  const [quotaInputMode, setQuotaInputMode] = useState("amount");
  const [quotaAccordionOpen, setQuotaAccordionOpen] = useState(true);
  const [conceptsAccordionOpen, setConceptsAccordionOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setComponentInputModes(emptyComponentInputModes);
    setCondoneAllByComponent(emptyCondoneAllByComponent);
    setCondoneAllQuota(false);
    setQuotaDiscountInput("");
    setQuotaInputMode("amount");
    setQuotaAccordionOpen(true);
    setConceptsAccordionOpen(false);
    setForm(emptyForm);
    axiosClient
      .get(`/v1/condonations/config/components`)
      .then((response) => {
        setComponentConfigs(response.data || []);
      })
      .catch((error) => {
        setComponentConfigs([]);
        console.log("Error: " + error.message);
      });
  }, [open, activeQuota]);

  if (!activeQuota) {
    return null;
  }

  const quota = activeQuota.quota || {};
  const pending = activeQuota.pending || {};
  const quotaValue = toNumber(quota.value);
  const gacValue = toNumber(quota.gac ?? pending.pendingGac);
  const moraValue = toNumber(quota.mora ?? pending.pendingMora);
  const valorCuota = roundMoney(quotaValue + gacValue + moraValue);

  const expectedByComponent = COMPONENT_FIELDS.reduce((acc, field) => {
    acc[field.component] = roundMoney(expectedForField(field, quota, pending));
    return acc;
  }, {});
  const componentsExpectedSum = COMPONENT_FIELDS.reduce(
    (sum, field) => sum + expectedByComponent[field.component],
    0
  );

  const orderedComponents = [...COMPONENT_FIELDS]
    .map((field, index) => {
      const config = componentConfigs.find((item) => item.component === field.component);
      return {
        ...field,
        expected: expectedByComponent[field.component],
        enabled: config?.enabled !== false,
        priority: config?.priority ?? index + 1,
      };
    })
    .sort(
      (left, right) =>
        Number(left.component === "CAPITAL") - Number(right.component === "CAPITAL") ||
        left.priority - right.priority ||
        left.component.localeCompare(right.component)
    );

  const parsedQuotaDiscount = parseNumericInput(quotaDiscountInput);
  const quotaDiscountAmount = condoneAllQuota
    ? componentsExpectedSum
    : parsedQuotaDiscount == null
    ? 0
    : quotaInputMode === "percent"
    ? roundMoney(componentsExpectedSum * (parsedQuotaDiscount / 100))
    : roundMoney(parsedQuotaDiscount);
  const clampedQuotaDiscount = Math.min(componentsExpectedSum, Math.max(0, quotaDiscountAmount));
  const usesQuotaDiscount = condoneAllQuota || clampedQuotaDiscount > 0;
  const quotaAllocation = allocateCondonationByPriority(clampedQuotaDiscount, orderedComponents);

  const componentDiscountTotal = COMPONENT_FIELDS.reduce((sum, field) => {
    if (condoneAllByComponent[field.component]) {
      return sum + (expectedByComponent[field.component] || 0);
    }
    const isPercent = componentInputModes[field.component] === "percent";
    const parsed = parseNumericInput(isPercent ? form[field.percentKey] : form[field.amountKey]);
    if (parsed == null) {
      return sum;
    }
    if (isPercent) {
      return sum + roundMoney((expectedByComponent[field.component] || 0) * (parsed / 100));
    }
    return sum + roundMoney(parsed);
  }, 0);

  const totalCondoned = usesQuotaDiscount
    ? clampedQuotaDiscount
    : roundMoney(Math.min(valorCuota, Math.max(0, componentDiscountTotal)));
  const remainingToPay = roundMoney(Math.max(0, valorCuota - totalCondoned));

  const amountsByComponent = orderedComponents.reduce((acc, item) => {
    if (usesQuotaDiscount) {
      acc[item.component] = quotaAllocation.allocations[item.component] || 0;
      return acc;
    }
    const field = COMPONENT_FIELDS.find((entry) => entry.component === item.component);
    if (!field) {
      acc[item.component] = 0;
      return acc;
    }
    if (condoneAllByComponent[field.component]) {
      acc[field.component] = expectedByComponent[field.component] || 0;
      return acc;
    }
    const isPercent = componentInputModes[field.component] === "percent";
    const parsed = parseNumericInput(isPercent ? form[field.percentKey] : form[field.amountKey]);
    if (parsed == null) {
      acc[field.component] = 0;
      return acc;
    }
    acc[field.component] = isPercent
      ? roundMoney((expectedByComponent[field.component] || 0) * (parsed / 100))
      : roundMoney(parsed);
    return acc;
  }, {});

  const hierarchyViolation = findHierarchyViolation(orderedComponents, amountsByComponent);

  const clearLaterConceptValues = (component, currentForm, currentCondoneAll) => {
    const nextForm = { ...currentForm };
    const nextCondoneAll = { ...currentCondoneAll };
    getLaterComponents(component, orderedComponents).forEach((item) => {
      const field = COMPONENT_FIELDS.find((entry) => entry.component === item.component);
      if (!field) {
        return;
      }
      nextForm[field.percentKey] = "";
      nextForm[field.amountKey] = "";
      nextCondoneAll[field.component] = false;
    });
    return { nextForm, nextCondoneAll };
  };

  const handleField = (key) => (event) => {
    const value = key === "freezeGacAndMora" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleNumericField = (field) => (event) => {
    const isPercent = componentInputModes[field.component] === "percent";
    const key = isPercent ? field.percentKey : field.amountKey;
    const formatted = formatNumericInput(event.target.value);
    const parsed = parseNumericInput(formatted);
    const allocated = isPercent
      ? roundMoney((expectedByComponent[field.component] || 0) * ((parsed || 0) / 100))
      : parsed || 0;
    const stillComplete = isComponentFullyCondoned(allocated, expectedByComponent[field.component]);

    setForm((prev) => {
      const updated = { ...prev, [key]: formatted };
      if (stillComplete) {
        return updated;
      }
      return clearLaterConceptValues(field.component, updated, condoneAllByComponent).nextForm;
    });
    if (!stillComplete) {
      setCondoneAllByComponent((prev) => ({
        ...clearLaterConceptValues(field.component, form, prev).nextCondoneAll,
        [field.component]: false,
      }));
    }
  };

  const handleComponentInputMode = (component) => (event, newInputMode) => {
    if (!newInputMode || usesQuotaDiscount || condoneAllByComponent[component]) {
      return;
    }
    if (getHierarchyBlockers(component, orderedComponents, amountsByComponent).length > 0) {
      return;
    }
    setComponentInputModes((prev) => ({ ...prev, [component]: newInputMode }));
  };

  const handleCondoneAllComponent = (field) => (event) => {
    if (getHierarchyBlockers(field.component, orderedComponents, amountsByComponent).length > 0) {
      return;
    }
    const checked = event.target.checked;
    const esperado = expectedByComponent[field.component];
    setComponentInputModes((prev) => ({ ...prev, [field.component]: "amount" }));
    setCondoneAllByComponent((prev) => {
      const updated = { ...prev, [field.component]: checked };
      if (checked) {
        return updated;
      }
      return clearLaterConceptValues(field.component, form, updated).nextCondoneAll;
    });
    setForm((prev) => {
      const updated = {
        ...prev,
        [field.percentKey]: "",
        [field.amountKey]: checked ? formatNumberToInput(esperado) : "",
      };
      if (checked) {
        return updated;
      }
      return clearLaterConceptValues(field.component, updated, condoneAllByComponent).nextForm;
    });
  };

  const handleCondoneAllQuota = (event) => {
    const checked = event.target.checked;
    setCondoneAllQuota(checked);
    setQuotaInputMode("amount");
    setQuotaDiscountInput(checked ? formatNumberToInput(componentsExpectedSum) : "");
    setCondoneAllByComponent(emptyCondoneAllByComponent);
  };

  const displayedComponentValue = (field) => {
    if (usesQuotaDiscount) {
      return formatNumberToInput(quotaAllocation.allocations[field.component] || 0);
    }
    const isPercent = componentInputModes[field.component] === "percent";
    return isPercent ? form[field.percentKey] : form[field.amountKey];
  };

  const buildRequest = () => {
    const request = {
      expirationDate: form.expirationDate,
      freezeGacAndMora: form.freezeGacAndMora,
      reason: form.reason.trim(),
      createdByUserUuid: localStorage.getItem("userUuid") || null,
    };

    if (usesQuotaDiscount) {
      if (condoneAllQuota || quotaInputMode === "amount") {
        request.totalAmount = clampedQuotaDiscount;
      } else {
        request.totalPercent = parsedQuotaDiscount;
      }
      return request;
    }

    COMPONENT_FIELDS.forEach((field) => {
      if (condoneAllByComponent[field.component]) {
        request[field.amountKey] = expectedByComponent[field.component];
        return;
      }
      const isPercent = componentInputModes[field.component] === "percent";
      const key = isPercent ? field.percentKey : field.amountKey;
      request[key] = parseNumericInput(form[key]);
    });
    return request;
  };

  const validate = () => {
    if (!form.expirationDate) {
      Swal.fire({
        icon: "warning",
        title: "Falta la fecha",
        text: "Debes indicar la fecha de vencimiento de la oferta.",
        confirmButtonColor: "#161e55",
      });
      return false;
    }
    if (!form.reason.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Falta el motivo",
        text: "Debes indicar el motivo de la oferta.",
        confirmButtonColor: "#161e55",
      });
      return false;
    }
    if (hierarchyViolation) {
      Swal.fire({
        icon: "warning",
        title: "Jerarquía de condonación",
        text: `No puedes condonar ${
          COMPONENT_LABELS[hierarchyViolation.skipped.component]
        } sin condonar primero por completo: ${hierarchyBlockersLabel(hierarchyViolation.blockers)}.`,
        confirmButtonColor: "#161e55",
      });
      return false;
    }
    return true;
  };

  const handleCreate = () => {
    if (!validate()) {
      return;
    }
    setSubmitting(true);
    axiosClient
      .post(`/v1/condonations/quotas/${activeQuota.quota.uuid}/offers`, buildRequest())
      .then(() => {
        setSubmitting(false);
        onSuccess("Se creó la oferta de condonación.");
        onClose();
      })
      .catch((error) => {
        setSubmitting(false);
        Swal.fire({
          icon: "error",
          title: "No se pudo crear la oferta",
          text: error.response?.data?.message || "Revisa los datos ingresados.",
          confirmButtonColor: "#161e55",
        });
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{`Oferta de condonación - cuota #${quota.quotaNumber ?? ""}`}</DialogTitle>
      <DialogContent sx={squareFieldSx}>
        <Box
          sx={{
            mt: 0.5,
            mb: 2,
            p: 2,
            bgcolor: PRAMI_COLORS.pageBg,
            border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Valor de la cuota
          </Typography>
          <Typography variant="h6" sx={{ color: PRAMI_COLORS.primary, fontWeight: 700, lineHeight: 1.2 }}>
            {formatMoney(valorCuota)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {`${formatMoney(quotaValue)} (cuota) + ${formatMoney(gacValue)} (GAC) + ${formatMoney(moraValue)} (mora)`}
          </Typography>

          <Divider sx={{ my: 1.5, borderColor: PRAMI_COLORS.surfaceBorder }} />

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Esperado por componente
          </Typography>
          <Grid container spacing={1}>
            {orderedComponents.map((field) => (
              <Grid item xs={6} sm={4} key={field.component}>
                <Typography variant="caption" color="text.secondary">
                  {`${COMPONENT_LABELS[field.component] || field.component}${
                    field.component === "CAPITAL"
                      ? " · siempre al final"
                      : field.priority
                      ? ` · prioridad ${field.priority}`
                      : ""
                  }`}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: PRAMI_COLORS.primary }}>
                  {formatMoney(expectedByComponent[field.component] || 0)}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Accordion
          expanded={quotaAccordionOpen}
          onChange={(_, expanded) => setQuotaAccordionOpen(expanded)}
          sx={{ ...accordionSx, mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: PRAMI_COLORS.primary, fontWeight: 600 }}>
                Descuento sobre la cuota
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valor o porcentaje a condonar; se reparte por prioridad de componentes.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
          <FormControlLabel
            control={<Checkbox checked={condoneAllQuota} onChange={handleCondoneAllQuota} />}
            label="Condonar todo lo condonable de la cuota"
          />
          <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                type="text"
                inputProps={{ inputMode: "decimal" }}
                disabled={condoneAllQuota}
                label={
                  quotaInputMode === "percent"
                    ? "Porcentaje a condonar de la cuota"
                    : "Valor a condonar de la cuota"
                }
                value={quotaDiscountInput}
                onChange={(event) => setQuotaDiscountInput(formatNumericInput(event.target.value))}
                helperText={`El cliente pagaría ${formatMoney(remainingToPay)} de ${formatMoney(valorCuota)}`}
                InputProps={{
                  endAdornment: (
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={quotaInputMode}
                      onChange={(event, newInputMode) => {
                        if (!newInputMode || condoneAllQuota) {
                          return;
                        }
                        setQuotaInputMode(newInputMode);
                        setQuotaDiscountInput("");
                      }}
                    >
                      <ToggleButton value="percent" sx={{ px: 1, py: 0.25 }}>
                        %
                      </ToggleButton>
                      <ToggleButton value="amount" sx={{ px: 1, py: 0.25 }}>
                        $
                      </ToggleButton>
                    </ToggleButtonGroup>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {usesQuotaDiscount ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {`Aplicación del descuento ${formatMoney(clampedQuotaDiscount)} por orden de condonación`}
              </Typography>
              <Grid container spacing={1}>
                {orderedComponents.map((field) => {
                  const allocated = quotaAllocation.allocations[field.component] || 0;
                  if (allocated <= 0 && (expectedByComponent[field.component] || 0) <= 0) {
                    return null;
                  }
                  return (
                    <Grid item xs={12} sm={6} key={`alloc-${field.component}`}>
                      <Typography variant="body2">
                        <strong>{COMPONENT_LABELS[field.component] || field.component}:</strong>
                        {` ${formatMoney(allocated)} de ${formatMoney(expectedByComponent[field.component] || 0)}`}
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : null}
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={conceptsAccordionOpen}
          onChange={(_, expanded) => setConceptsAccordionOpen(expanded)}
          sx={{ ...accordionSx, mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: PRAMI_COLORS.primary, fontWeight: 600 }}>
                Descuento por conceptos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Debes condonar en orden de prioridad. No se puede saltar GAC, mora u otro rubro anterior.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
        <Grid container spacing={2}>
          {COMPONENT_FIELDS.map((field) => {
            const isPercent = componentInputModes[field.component] === "percent";
            const esperado = expectedByComponent[field.component] || 0;
            const condoneAll = condoneAllByComponent[field.component];
            const blockers = getHierarchyBlockers(field.component, orderedComponents, amountsByComponent);
            const lockedByHierarchy = !usesQuotaDiscount && blockers.length > 0;
            const disabled = usesQuotaDiscount || condoneAll || lockedByHierarchy;
            return (
              <Grid item xs={12} sm={6} key={field.component}>
                <Box
                  sx={{
                    height: "100%",
                    p: 1.5,
                    border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
                    minWidth: 0,
                    opacity: lockedByHierarchy ? 0.7 : 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: PRAMI_COLORS.primary,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      mb: 0.5,
                      wordBreak: "break-word",
                    }}
                  >
                    {COMPONENT_LABELS[field.component]}
                  </Typography>
                  <FormControlLabel
                    sx={{
                      alignItems: "center",
                      ml: -0.5,
                      mb: 0.5,
                      mr: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.875rem",
                      },
                    }}
                    control={
                      <Checkbox
                        checked={condoneAll}
                        disabled={usesQuotaDiscount || lockedByHierarchy}
                        onChange={handleCondoneAllComponent(field)}
                      />
                    }
                    label="Condonar todo el concepto"
                  />
                  <TextField
                    fullWidth
                    type="text"
                    inputProps={{ inputMode: "decimal" }}
                    disabled={disabled}
                    label={isPercent ? "Porcentaje a condonar" : "Valor a condonar"}
                    value={lockedByHierarchy ? "" : displayedComponentValue(field)}
                    onChange={handleNumericField(field)}
                    helperText={
                      lockedByHierarchy
                        ? `Primero condona por completo: ${hierarchyBlockersLabel(blockers)}`
                        : `Esperado: ${formatMoney(esperado)}`
                    }
                    error={lockedByHierarchy}
                    InputProps={{
                      endAdornment: (
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={componentInputModes[field.component]}
                          onChange={handleComponentInputMode(field.component)}
                        >
                          <ToggleButton value="percent" sx={{ px: 1, py: 0.25 }} disabled={disabled}>
                            %
                          </ToggleButton>
                          <ToggleButton value="amount" sx={{ px: 1, py: 0.25 }} disabled={disabled}>
                            $
                          </ToggleButton>
                        </ToggleButtonGroup>
                      ),
                    }}
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>
          </AccordionDetails>
        </Accordion>

        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: PRAMI_COLORS.rowHighlight,
            border: `1px solid ${PRAMI_COLORS.sageSelected}`,
            borderRadius: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Después de los descuentos, el cliente pagaría de esta cuota
          </Typography>
          <Typography variant="h6" sx={{ color: PRAMI_COLORS.primary, fontWeight: 700, lineHeight: 1.2 }}>
            {formatMoney(remainingToPay)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {`Cuota ${formatMoney(valorCuota)} − descuento ${formatMoney(totalCondoned)}`}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="date"
              label="Fecha de vencimiento"
              InputLabelProps={{ shrink: true }}
              value={form.expirationDate}
              onChange={handleField("expirationDate")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Motivo"
              value={form.reason}
              onChange={handleField("reason")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonAdd disabled={submitting} onClick={handleCreate}>
          Crear oferta
        </ButtonAdd>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
